import { LazyExoticComponent, FC } from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { routePath, authPaths } from 'config/route-config';

type ComponentType<P> = (FC<P> | LazyExoticComponent<FC<P>>) & {
  preload?: () => Promise<unknown>;
};

export type Route = {
  path: string;
  name: string;
  featureFlag?: string;
  isLanding?: boolean;
  isEnding?: boolean;
  noIndex?: boolean;
  hasFooter?: boolean;
  requiresLogin?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
};

export const authRoutes: Route[] = [
  {
    name: 'Auth Response',
    path: authPaths.authResponse,
    component: lazy(() => import('components/app/routes/AuthResponse')),
    isLanding: true,
    noIndex: true
  }
];

export const partnerLandingRoutes: Route[] = [
  {
    name: 'Move In',
    path: routePath.moveIn,
    component: lazy(() => import('components/move-in/MoveIn')),
    hasFooter: true,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Voicesales Acquisition',
    path: routePath.voicesalesAcquisition,
    component: lazy(
      () => import('components/voicesales-acquisition/VoiceSalesAcquisition')
    ),
    hasFooter: true,
    isLanding: true,
    requiresLogin: true,
    noIndex: true
  },
  {
    name: 'Voicesales Winback',
    path: routePath.voiceSalesWinback,
    component: lazy(
      () => import('components/voicesales-winback/VoiceSalesWinback')
    ),
    hasFooter: true,
    isLanding: true,
    requiresLogin: true,
    noIndex: true
  },
  {
    name: 'Voicesales Homemove',
    path: routePath.voiceSalesHomeMove,
    component: lazy(
      () => import('components/voicesales-homemove/VoiceSalesHomeMove')
    ),
    hasFooter: true,
    isLanding: true,
    requiresLogin: true,
    noIndex: true
  },
  {
    name: 'Nondomestic Transfer',
    path: routePath.nondomesticTransfer,
    component: lazy(
      () => import('components/nondomestic-transfer/NondomesticTransfer')
    ),
    hasFooter: true,
    isLanding: true,
    requiresLogin: true,
    noIndex: true
  },
  {
    name: 'Tariff information',
    path: routePath.tariffInformation,
    component: lazy(
      () =>
        import('components/direct-sales/tariff-information/TariffInformation')
    ),
    hasFooter: true,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Solar registration',
    path: routePath.solarRegistration,
    component: lazy(
      () =>
        import('components/direct-sales/solar-registration/SolarRegistration')
    ),
    featureFlag: 'releaseSolarRegistration',
    hasFooter: true,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Heat pump registration',
    path: routePath.heatPumpRegistration,
    component: lazy(
      () =>
        import(
          'components/direct-sales/heat-pump-registration/HeatPumpRegistration'
        )
    ),
    featureFlag: 'releaseHeatPumpRegistration',
    hasFooter: true,
    isLanding: true,
    noIndex: true
  }
];

export const journeyRoutes: Route[] = [
  {
    name: 'Find address',
    path: routePath.findAddress,
    component: lazy(
      () => import('components/direct-sales/find-address/FindAddress')
    )
  },
  {
    name: 'Price Announcement',
    path: routePath.businessUpdatesAnnouncement,
    component: lazy(
      () =>
        import('components/direct-sales/business-updates/BusinessAnnouncement')
    )
  },
  {
    name: 'Email Submit',
    path: routePath.businessUpdatesSubmitEmail,
    component: lazy(
      () => import('components/direct-sales/business-updates/SubmitEmail')
    )
  },
  {
    name: 'Email Submit Success',
    path: routePath.businessUpdatesSubmitEmailSuccess,
    component: lazy(
      () =>
        import('components/direct-sales/business-updates/SubmitEmailSuccess')
    )
  },
  {
    name: 'Personalise Quote',
    path: routePath.personaliseQuote,
    component: lazy(
      () => import('components/direct-sales/personalise-quote/PersonaliseQuote')
    )
  },
  {
    name: 'Consumption Entry',
    path: routePath.consumptionEntry,
    component: lazy(
      () => import('components/direct-sales/consumption-entry/ConsumptionEntry')
    )
  },
  {
    name: 'Quote',
    path: routePath.quote,
    component: lazy(() => import('components/direct-sales/quote/Quote'))
  },
  {
    name: 'Tariff Details',
    path: routePath.tariffDetails,
    component: lazy(
      () => import('components/direct-sales/tariff-details/TariffDetails')
    )
  },
  {
    name: 'Create account',
    path: routePath.createAccount,
    component: lazy(
      () => import('components/direct-sales/create-account/CreateAccount')
    )
  },
  {
    name: 'Fuel Type',
    path: routePath.fuelType,
    component: lazy(() => import('components/direct-sales/fuel-type/FuelType'))
  },
  {
    name: 'Meterpoint Validation',
    path: routePath.meterpointValidation,
    component: lazy(
      () =>
        import(
          'components/direct-sales/meterpoint-validation/MeterpointValidation'
        )
    )
  },
  {
    name: 'Meterpoint Guide',
    path: routePath.meterpointGuide,
    component: lazy(
      () => import('components/direct-sales/meterpoint-guide/MeterpointGuide')
    )
  },
  {
    name: 'Mixed Meters',
    path: routePath.mixedMeters,
    component: lazy(
      () => import('components/direct-sales/mixed-meters/MixedMeters')
    )
  },
  {
    name: 'Prepay Info',
    path: routePath.prepayInfo,
    component: lazy(
      () => import('components/direct-sales/prepay-info/PrepayInfo')
    )
  },
  {
    name: 'Direct debit type',
    path: routePath.directDebitType,
    component: lazy(
      () => import('components/direct-sales/direct-debit-type/DirectDebitType')
    )
  },
  {
    name: 'Direct debit',
    path: routePath.directDebit,
    component: lazy(
      () => import('components/direct-sales/direct-debit/DirectDebit')
    )
  },
  {
    name: 'Psr entry',
    path: routePath.psrEntry,
    component: lazy(() => import('components/direct-sales/psr-entry/Psr'))
  },

  {
    name: 'Marketing preferences',
    path: routePath.userPreferences,
    component: lazy(
      () => import('components/direct-sales/user-preferences/UserPreferences')
    )
  },
  {
    name: 'Order summary',
    path: routePath.orderSummary,
    component: lazy(
      () => import('components/direct-sales/order-summary/OrderSummary')
    )
  },
  {
    name: 'Order complete',
    path: routePath.orderComplete,
    component: lazy(
      () => import('components/direct-sales/order-complete/OrderComplete')
    ),
    hasFooter: true,
    isEnding: true
  },
  {
    name: 'Heat pump confirmation',
    path: routePath.heatPumpConfirmation,
    component: lazy(
      () =>
        import(
          'components/direct-sales/heat-pump-confirmation/HeatPumpConfirmation'
        )
    ),
    featureFlag: 'releaseHeatPumpRegistration',
    hasFooter: true,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Solar confirmation',
    path: routePath.solarConfirmation,
    component: lazy(
      () =>
        import('components/direct-sales/solar-confirmation/SolarConfirmation')
    ),
    featureFlag: 'releaseSolarRegistration',
    hasFooter: true,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Home move date',
    path: routePath.moveDate,
    component: lazy(
      () => import('components/direct-sales/move-in-date/MoveInDate')
    ),
    hasFooter: true
  },
  {
    name: 'New Home',
    path: routePath.newHome,
    component: lazy(() => import('components/direct-sales/new-home/NewHome'))
  },
  {
    name: 'Exceptions',
    path: routePath.exceptions,
    component: lazy(
      () => import('components/direct-sales/exceptions/Exceptions')
    ),
    hasFooter: true
  },
  {
    name: 'Split Supply',
    path: routePath.splitSupply,
    component: lazy(
      () => import('components/direct-sales/split-supply/SplitSupply')
    ),
    hasFooter: true
  },
  {
    name: 'Classic Supply',
    path: routePath.classicSupply,
    component: lazy(
      () => import('components/direct-sales/classic-supply/ClassicSupply')
    ),
    hasFooter: true
  },
  {
    name: 'Non Supply',
    path: routePath.nonSupply,
    component: lazy(
      () => import('components/direct-sales/non-supply/NonSupply')
    ),
    hasFooter: true
  },
  {
    name: 'Meter Reads',
    path: routePath.meterReads,
    component: lazy(
      () => import('components/direct-sales/meter-reads/MeterReads')
    )
  },
  {
    name: 'Select Options',
    path: routePath.selectOptions,
    component: lazy(
      () => import('components/direct-sales/select-options/SelectOptions')
    )
  },
  {
    name: 'EV Tariff Information',
    path: routePath.evTariffInformation,
    component: lazy(
      () =>
        import(
          'components/direct-sales/ev-tariff-information/EvTariffInformation'
        )
    )
  }
];

export const routes = [
  ...journeyRoutes,
  ...partnerLandingRoutes,
  ...authRoutes
];
